import { Alert, Box, Collapse, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/molecules/dialog';
import {
  FormValues,
  useTotpCodeDialogValidationSchema,
} from './schema/use-form-validation-schema';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { InputField } from 'src/components/atoms/input-field';
import { useTotpDialog } from 'src/containers/contexts/totp-dialog-provider';
import { useKeyDown } from 'src/hooks/use-key-down';

const defaultValues: FormValues = {
  totpCode: '',
};

export default function TotpRequiredDialog() {
  const { t } = useTranslation();

  const { state, submitCode, cancel } = useTotpDialog();

  const validationSchema = useTotpCodeDialogValidationSchema();

  const { reset, control, handleSubmit } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (values: FormValues) => {
    submitCode(values.totpCode);
  };

  useKeyDown('Enter', handleSubmit(onSubmit));

  useEffect(() => {
    reset();
  }, [state.open, reset]);

  return (
    <Dialog
      title={state?.title || t('common:codeIsRequiredTitle')}
      open={state.open}
      cancelButtonText={t('common:button.cancel')}
      confirmButtonText={t('common:button.submit')}
      onCancel={cancel}
      onConfirm={handleSubmit(onSubmit)}
      isConfirmButtonSubmitting={state.isSubmitting}
      isConfirmButtonDisabled={state.isSubmitting}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
        <Typography variant="body2">
          {state?.description || t('common:codeIsRequiredDescription')}
        </Typography>
        <Controller
          control={control}
          name="totpCode"
          render={({
            field: { onChange, value, onBlur, name },
            fieldState,
          }) => (
            <InputField
              name={name}
              label={t('common:code')}
              fullWidth
              required
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              error={!!fieldState?.error}
              helperText={fieldState?.error?.message}
              sx={{ my: 1 }}
            />
          )}
        />
        <Collapse timeout={200} in={!!state?.errorMessage}>
          <Alert
            severity="error"
            sx={{
              opacity: state?.errorMessage ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}>
            {state?.errorMessage ?? ''}
          </Alert>
        </Collapse>
      </Box>
    </Dialog>
  );
}
